import type { ReactNode } from "react"
import { ConfigProvider, App as AntdApp } from "antd"
import enUS from "antd/locale/en_US"
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { antdTheme } from "@csbi/viz-core"
import { ApiError } from "../../shared/api/request.js"
import { ThemeProvider, useTheme } from "./theme-provider.js"

const ME = ["auth", "me"] as const

/**
 * ⚠ `/auth/me` itself has to be skipped: invalidating it when it 401s is an infinite refetch loop.
 * `RequireAuth` already handles that case.
 */
const onApiError = (error: unknown, key?: readonly unknown[]) => {
  if (!(error instanceof ApiError) || error.status !== 401) return
  if (key && key[0] === ME[0] && key[1] === ME[1]) return
  void queryClient.invalidateQueries({ queryKey: ME })
}

const queryClient: QueryClient = new QueryClient({
  queryCache: new QueryCache({ onError: (error, query) => onApiError(error, query.queryKey) }),
  mutationCache: new MutationCache({ onError: (error) => onApiError(error) }),
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      staleTime: 60_000,
    },
  },
})

function AntdShell({ children }: { children: ReactNode }) {
  const { mode } = useTheme()
  const { token, components } = antdTheme(mode)

  return (
    <ConfigProvider
      locale={enUS}
      theme={{ token, components }}
    >
      <AntdApp>{children}</AntdApp>
    </ConfigProvider>
  )
}

export function AppProvider({ children }: { children: ReactNode }) {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AntdShell>{children}</AntdShell>
      </ThemeProvider>
    </QueryClientProvider>
  )
}

export { queryClient }
